import React, { useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  alpha,
  Divider,
} from "@mui/material";
import { useForm } from "react-hook-form";
import axios from "axios";
import { showToast, WEBSERVICE } from "../../CoreDataService";
import { useNavigate } from "react-router-dom";
import {
  allowOnlyAlphabetsCityState,
  OnlyAllowedInput,
  PreventPasteCityState,
  sanitizeBlockedUnicode,
  handleBlockedUnicodePaste,
} from ".";
import { useSelector } from "react-redux";
import { ContainedButton } from "../../Common/Buttons";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";

const IdentityVerificationDOB = ({
  handleSteps,
  handleBack,
  userDetails,
  setIsloading,
}) => {
  const navigate = useNavigate();
  const { btncolor } = useSelector((state) => state);
  const uuid = localStorage.getItem("uuid");
  const token = localStorage.getItem("access_token");

  const {
    handleSubmit,
    register,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      month: "",
      day: "",
      year: "",
      ssnOrPassport: "",
      birthPlace: "",
    },
  });

  useEffect(() => {
    if (userDetails) {
      reset({
        month: userDetails?.dob?.split("-")[0]?.trim() || "",
        day: userDetails?.dob?.split("-")[1]?.trim() || "",
        year: userDetails?.dob?.split("-")[2]?.trim() || "",
        ssnOrPassport: userDetails?.ssn || "",
        birthPlace: userDetails?.birthPlace || "",
      });
    }
  }, [userDetails, reset]);

  const isAtLeast18 = (month, day, year) => {
    if (!month || !day || !year) return true;
    const dob = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age >= 18;
  };

  const onSubmit = async (data) => {
    const dob = `${data.month}-${data.day}-${data.year}`;
    const payload = {
      ...userDetails,
      dob,
      ssn: sanitizeBlockedUnicode(data.ssnOrPassport),
      birthPlace: sanitizeBlockedUnicode(data.birthPlace),
      uuid,
    };
    setIsloading(true);
    try {
      const response = await axios.post(
        `${WEBSERVICE}user/saveUserKycData`,
        payload,
        { headers: { Authorization: `BEARER ${token}` } }
      );
      if (response?.data?.error?.error_data === 0) {
        setIsloading(false);
        showToast("success", response?.data?.error?.error_msg);
        handleSteps();
      } else {
        setIsloading(false);
        showToast("error", response?.data?.error?.error_msg);
      }
    } catch (error) {
      setIsloading(false);
      showToast("error", error?.response?.data?.error?.error_msg);
    }
  };

  const handlePrevent = (e) => {
    const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];
    const value = e.target.value;
    if (
      !allowedKeys.includes(e.key) &&
      value.length >= 15 &&
      window.getSelection().toString().length === 0
    ) {
      e.preventDefault();
    }
  };

  return (
    <Box sx={card}>
      {/* Step Header */}
      <Box sx={stepHeader}>
        <Box sx={{ ...stepBadge, backgroundColor: alpha(btncolor || "#1976d2", 0.12), color: btncolor }}>
          <EventOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>
          <Typography sx={stepTitle}>Date of Birth & Identity</Typography>
          <Typography sx={stepCaption}>
            Enter your DOB and SSN / Passport / National ID
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mb: 3 }} />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={2.5}>

          {/* Date of Birth */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography sx={fieldLabel}>Date of Birth *</Typography>
            <Typography sx={fieldHint}>MM / DD / YYYY format</Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
              <TextField
                placeholder="MM"
                inputProps={{ maxLength: 2 }}
                {...register("month", {
                  required: "Month is required",
                  pattern: { value: /^(0[1-9]|1[0-2])$/, message: "Invalid month" },
                })}
                error={!!errors.month}
                onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9]/g, ""); }}
                onBlur={(e) => {
                  if (!/^(0[1-9]|1[0-2])$/.test(e.target.value)) e.target.value = "";
                }}
                sx={{ ...modernField, flex: 1 }}
              />
              <TextField
                placeholder="DD"
                inputProps={{ maxLength: 2 }}
                {...register("day", {
                  required: "Day is required",
                  pattern: { value: /^(0[1-9]|[12][0-9]|3[01])$/, message: "Invalid day" },
                })}
                error={!!errors.day}
                onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9]/g, ""); }}
                onBlur={(e) => {
                  if (!/^(0[1-9]|[12][0-9]|3[01])$/.test(e.target.value)) e.target.value = "";
                }}
                sx={{ ...modernField, flex: 1 }}
              />
              <TextField
                placeholder="YYYY"
                inputProps={{ maxLength: 4 }}
                {...register("year", {
                  required: "Year is required",
                  pattern: { value: /^\d{4}$/, message: "Invalid year" },
                  validate: {
                    isOldEnough: (value) =>
                      isAtLeast18(getValues("month"), getValues("day"), value) ||
                      "Age should be equal or more than 18",
                    minYear: (value) =>
                      parseInt(value, 10) >= 1920 || "DOB should be after 1920",
                  },
                })}
                error={!!errors.year}
                onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9]/g, ""); }}
                onBlur={(e) => {
                  if (!/^\d{4}$/.test(e.target.value)) e.target.value = "";
                }}
                sx={{ ...modernField, flex: 2 }}
              />
            </Box>
            {(errors.month || errors.day || errors.year) && (
              <Typography sx={errorText}>
                {errors.month?.message || errors.day?.message || errors.year?.message}
              </Typography>
            )}
          </Grid>

          {/* Place of Birth */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography sx={fieldLabel}>Place of Birth *</Typography>
            <TextField
              placeholder="Enter place of birth"
              fullWidth
              {...register("birthPlace", {
                required: "Place is required",
                pattern: { value: /^[A-Za-z\s]+$/, message: "Only alphabets are allowed" },
              })}
              error={!!errors.birthPlace}
              helperText={errors.birthPlace?.message}
              onKeyDown={allowOnlyAlphabetsCityState}
              onPaste={PreventPasteCityState}
              autoComplete="off"
              sx={{ ...modernField, mt: "calc(0.75rem + 18px)" }}
            />
          </Grid>

          {/* SSN / Passport */}
          <Grid size={{ xs: 12, sm: 12, md: 6 }}>
            <Typography sx={fieldLabel}>
              Social Security No. / Passport No. / National ID *
            </Typography>
            <Typography sx={fieldHint}>
              Enter the last four (4) digits of your SSN or your complete passport number
            </Typography>
            <TextField
              placeholder="Enter last 4 digits of SSN or complete passport number"
              fullWidth
              {...register("ssnOrPassport", {
                required: "SSN or Passport is required",
                minLength: { value: 4, message: "Must be at least 4 characters" },
                maxLength: { value: 15, message: "Must be not more than 15 characters" },
              })}
              error={!!errors.ssnOrPassport}
              helperText={errors.ssnOrPassport?.message}
              onKeyDown={(e) => { OnlyAllowedInput(e); handlePrevent(e); }}
              onPaste={handleBlockedUnicodePaste}
              autoComplete="off"
              inputProps={{ maxLength: 15 }}
              sx={{ ...modernField, mt: 0.75 }}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 3.5,
            pt: 2.5,
            borderTop: "1px solid rgba(125,125,125,0.12)",
            display: "flex",
            gap: 1.5,
          }}
        >
          <ContainedButton
            variant="contained"
            onClick={handleBack}
            sx={{ ...submitBtn, opacity: 0.85 }}
          >
            Back
          </ContainedButton>
          <ContainedButton type="submit" variant="contained" sx={submitBtn}>
            Save and Next
          </ContainedButton>
        </Box>
      </form>
    </Box>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const card = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  p: { xs: 2.5, md: 3 },
};

const stepHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  mb: 2.5,
  flexWrap: "wrap",
};

const stepBadge = {
  width: 44,
  height: 44,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const stepTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const stepCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.55,
  mt: 0.2,
};

const stepNum = {
  ml: "auto",
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  px: 1.5,
  py: 0.6,
  borderRadius: "20px",
};

const fieldLabel = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--text-color)",
  opacity: 0.55,
  mb: 0.3,
};

const fieldHint = {
  fontSize: "0.7rem",
  color: "var(--text-color)",
  opacity: 0.4,
  lineHeight: 1.45,
  mb: 0,
};

const errorText = {
  fontSize: "0.72rem",
  color: "#ef4444",
  mt: 0.5,
};

const modernField = {
  "& .MuiInputBase-root": {
    height: "48px",
    borderRadius: "10px",
    backgroundColor: "var(--body-color-less2)",
    color: "var(--text-color)",
    fontSize: "0.9rem",
  },
  "& .MuiInputBase-input": { color: "var(--text-color)" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(125,125,125,0.25)" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(125,125,125,0.45)" },
  "& .MuiInputLabel-root": { display: "none" },
  "& legend": { display: "none" },
  "& fieldset": { top: 0 },
  "& .MuiFormHelperText-root": { color: "#ef4444" },
};

const submitBtn = {
  height: 48,
  px: 4,
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "0.88rem",
  textTransform: "none",
};

export default IdentityVerificationDOB;
